import {
  DASHBOARD_REPORT_PAGE_SIZE as REPORT_PAGE_SIZE,
  sanitizeFileSegment,
} from '@/renderer/lib/utils';
import { Button, EmptyState, LoadingState } from '@components/common';
import { DatePicker } from '@components/common/DatePicker';
import { useAppSelector } from '@hooks/useRedux';
import { selectCurrentUser } from '@store/auth/authSelectors';
import { selectEffectiveOutlet, selectEffectiveOutletId } from '@store/outlets';
import { Download, RefreshCw, Search } from 'lucide-react';
import {
  type KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { exportReportCsv } from '../../../services/reportsService';
import type {
  ReportMetric,
  ReportQueryParams,
  ReportRecordsPageProps,
} from '../../../types/reports';
import { apiError, apiSuccess } from '../../../util/notify';
import {
  getDefaultReportDateRange,
  getReportAlignmentClass,
} from '../../../util/reporting';
import { SummaryMetricCard } from './SummaryMetricCard';

// Long enough that typing a bill number sends one request, not one per digit.
const SERVER_SEARCH_DEBOUNCE_MS = 400;

export const ReportRecordsPage = <T extends object>({
  title,
  description,
  searchPlaceholder,
  emptyTitle,
  emptyDescription,
  fetchRows,
  columns,
  exportConfig,
  buildMetrics,
  fetchMetrics,
  getRowKey,
  getSearchValue,
  searchMode = 'client',
  buildQueryParams,
  toolbarContent,
}: ReportRecordsPageProps<T>) => {
  const currentUser = useAppSelector(selectCurrentUser);
  const selectedOutletId = useAppSelector(selectEffectiveOutletId);
  const selectedOutlet = useAppSelector(selectEffectiveOutlet);

  const [dateRange, setDateRange] = useState(() =>
    getDefaultReportDateRange(selectedOutlet?.day_open),
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [rows, setRows] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [serverMetrics, setServerMetrics] = useState<ReportMetric[] | null>(
    null,
  );
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(false);
  const [metricsError, setMetricsError] = useState<string | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const latestRequestRef = useRef(0);
  const latestMetricsRequestRef = useRef(0);
  // The full-page spinner is only for the very first load. Afterwards (search,
  // filters, dates) results update in place so the search box keeps focus.
  const hasLoadedOnceRef = useRef(false);

  useEffect(() => {
    hasLoadedOnceRef.current = false;
    setDateRange(getDefaultReportDateRange(selectedOutlet?.day_open));
  }, [selectedOutlet?.day_open, selectedOutletId]);

  useEffect(() => {
    if (searchMode !== 'server') {
      setDebouncedSearchQuery(searchQuery);
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, SERVER_SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [searchMode, searchQuery]);

  const isDateRangeValid = dateRange.startDate <= dateRange.endDate;
  // Only the settled (debounced, trimmed) search reaches the server, and the
  // params are keyed by value: a new object with the same contents — from a
  // keystroke, or a parent passing an inline buildQueryParams — must not
  // rebuild loadPage/loadMetrics and fire another round of report queries.
  const serverSearchQuery =
    searchMode === 'server' ? debouncedSearchQuery.trim() : '';
  const additionalQueryParamsKey = JSON.stringify(
    buildQueryParams?.({ searchQuery: serverSearchQuery }) || {},
  );
  const additionalQueryParams = useMemo(
    () => JSON.parse(additionalQueryParamsKey) as Partial<ReportQueryParams>,
    [additionalQueryParamsKey],
  );

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    // Enter searches straight away instead of waiting out the debounce.
    if (event.key === 'Enter' && searchMode === 'server') {
      setDebouncedSearchQuery(searchQuery);
    }
  };

  const loadPage = useCallback(
    async ({
      offset,
      append,
      forceRefresh = false,
    }: {
      offset: number;
      append: boolean;
      forceRefresh?: boolean;
    }) => {
      if (!currentUser?.company?.id || !isDateRangeValid) {
        setRows([]);
        setHasMore(false);
        setError(
          isDateRangeValid
            ? 'Company context is missing for the current user.'
            : 'Start date must be earlier than or equal to end date.',
        );
        hasLoadedOnceRef.current = true;
        setIsLoading(false);
        setIsLoadingMore(false);
        return;
      }

      if (append) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }

      setError(null);
      latestRequestRef.current += 1;
      const requestId = latestRequestRef.current;

      const response = await fetchRows({
        companyId: currentUser.company.id,
        outletId: selectedOutletId,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        offset,
        limit: REPORT_PAGE_SIZE,
        forceRefresh,
        ...additionalQueryParams,
      });

      if (requestId !== latestRequestRef.current) {
        return;
      }

      if (!response.success || !response.data) {
        if (!append) {
          setRows([]);
        }
        setHasMore(false);
        setError(response.error || 'Failed to load report records.');
        hasLoadedOnceRef.current = true;
        setIsLoading(false);
        setIsLoadingMore(false);
        return;
      }

      const pageData = response.data;
      setRows((current) =>
        append ? [...current, ...pageData.rows] : pageData.rows,
      );
      setHasMore(pageData.hasMore);
      hasLoadedOnceRef.current = true;
      setIsLoading(false);
      setIsLoadingMore(false);
    },
    [
      currentUser?.company?.id,
      dateRange.endDate,
      dateRange.startDate,
      fetchRows,
      additionalQueryParams,
      isDateRangeValid,
      selectedOutletId,
    ],
  );

  const loadMetrics = useCallback(
    async (forceRefresh = false) => {
      if (!fetchMetrics || !currentUser?.company?.id || !isDateRangeValid) {
        return;
      }

      latestMetricsRequestRef.current += 1;
      const requestId = latestMetricsRequestRef.current;
      setIsLoadingMetrics(true);

      const response = await fetchMetrics({
        companyId: currentUser.company.id,
        outletId: selectedOutletId,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        forceRefresh,
        ...additionalQueryParams,
      });

      if (requestId !== latestMetricsRequestRef.current) return;

      setIsLoadingMetrics(false);
      if (!response.success || !response.data) {
        // Keep the last known totals rather than blanking the cards.
        setMetricsError(response.error || 'Failed to load report totals.');
        return;
      }

      setMetricsError(null);
      setServerMetrics(response.data.metrics);
      setTotalCount(response.data.totalCount ?? null);
    },
    [
      additionalQueryParams,
      currentUser?.company?.id,
      dateRange.endDate,
      dateRange.startDate,
      fetchMetrics,
      isDateRangeValid,
      selectedOutletId,
    ],
  );

  useEffect(() => {
    loadPage({
      offset: 0,
      append: false,
    });
  }, [loadPage]);

  useEffect(() => {
    loadMetrics();
  }, [loadMetrics]);

  const handleRefresh = async () => {
    if (!currentUser?.company?.id || !isDateRangeValid) return;

    setIsRefreshing(true);
    await Promise.all([
      loadPage({
        offset: 0,
        append: false,
        forceRefresh: true,
      }),
      loadMetrics(true),
    ]);
    setIsRefreshing(false);
  };

  const handleLoadMore = useCallback(async () => {
    if (isLoading || isLoadingMore || !hasMore) return;

    await loadPage({
      offset: rows.length,
      append: true,
    });
  }, [hasMore, isLoading, isLoadingMore, loadPage, rows.length]);

  const filteredRows = useMemo(() => {
    if (searchMode === 'server') {
      return rows;
    }

    const normalizedSearch = searchQuery.trim().toLowerCase();
    if (!normalizedSearch) return rows;

    return rows.filter((row) =>
      getSearchValue(row).toLowerCase().includes(normalizedSearch),
    );
  }, [getSearchValue, rows, searchMode, searchQuery]);

  useEffect(() => {
    const node = scrollContainerRef.current;
    if (!node) return undefined;

    const handleScroll = () => {
      const remainingDistance =
        node.scrollHeight - node.scrollTop - node.clientHeight;

      if (remainingDistance <= 160) {
        handleLoadMore();
      }
    };

    node.addEventListener('scroll', handleScroll);
    return () => {
      node.removeEventListener('scroll', handleScroll);
    };
  }, [handleLoadMore, rows.length]);

  const rowMetrics = useMemo(
    () => (!fetchMetrics && buildMetrics ? buildMetrics(rows) : []),
    [buildMetrics, fetchMetrics, rows],
  );
  const metrics = fetchMetrics ? serverMetrics : rowMetrics;
  const scopeLabel = selectedOutlet?.name || 'All outlets';

  const handleExportCsv = async () => {
    if (!exportConfig) return;
    if (!currentUser?.company?.id) {
      apiError('Company context is missing for CSV export.');
      return;
    }

    setIsExporting(true);

    const exportRows: T[] = [];
    let offset = 0;
    let canLoadMore = true;

    while (canLoadMore) {
      // eslint-disable-next-line no-await-in-loop
      const response = await fetchRows({
        companyId: currentUser.company.id,
        outletId: selectedOutletId,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        offset,
        limit: REPORT_PAGE_SIZE,
        ...additionalQueryParams,
      });

      if (!response.success || !response.data) {
        setIsExporting(false);
        apiError(response.error || 'Failed to load all rows for CSV export.');
        return;
      }

      exportRows.push(...response.data.rows);
      canLoadMore = response.data.hasMore;
      offset += response.data.rows.length;

      if (response.data.rows.length === 0) {
        break;
      }
    }

    const normalizedSearch = searchQuery.trim().toLowerCase();
    const rowsToExport =
      searchMode === 'server'
        ? exportRows
        : normalizedSearch
          ? exportRows.filter((row) =>
              getSearchValue(row).toLowerCase().includes(normalizedSearch),
            )
          : exportRows;

    if (rowsToExport.length === 0) {
      setIsExporting(false);
      apiError('No rows match the current filters for CSV export.');
      return;
    }

    const fileName = [
      exportConfig.fileNamePrefix,
      sanitizeFileSegment(scopeLabel),
      dateRange.startDate,
      'to',
      dateRange.endDate,
    ].join('-');

    const response = await exportReportCsv({
      title,
      fileName,
      columns: exportConfig.columns.map((column) => ({
        key: column.key,
        header: column.header,
      })),
      rows: rowsToExport.map((row) =>
        exportConfig.columns.reduce<
          Record<string, string | number | boolean | null>
        >((accumulator, column) => {
          accumulator[column.key] = column.value(row);
          return accumulator;
        }, {}),
      ),
    });

    setIsExporting(false);

    if (!response.success || !response.data) {
      apiError(response.error || 'Failed to export report CSV.');
      return;
    }

    apiSuccess('CSV export completed successfully.');
  };

  // Only show the full-screen spinner on the very first load (no data yet).
  // Filter/date changes reload in the background while existing rows stay visible.
  if (isLoading && !hasLoadedOnceRef.current) {
    return (
      <LoadingState message={`Loading ${title.toLowerCase()}...`} size="lg" />
    );
  }

  return (
    <div className="space-y-6">
      <section className="rounded-[22px] border border-slate-100 bg-white p-6 shadow-[0_2px_12px_rgba(15,23,42,0.04)]">
        <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
          <div className="max-w-3xl">
            <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-emerald-600">
              Business reports
            </p>
            <h2 className="mt-2 text-2xl font-semibold text-slate-900">
              {title}
            </h2>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              {description}
            </p>
            <div className="mt-4 flex flex-wrap gap-2 text-xs">
              <span className="rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1 font-medium text-emerald-700">
                Scope: {scopeLabel}
              </span>
            </div>
          </div>

          {/* <div className="grid grid-cols-1 gap-3 xl:min-w-[430px]"> */}
          <div className="flex flex-row gap-4 xl:min-w-[430px]">
            <DatePicker
              mode="single"
              id="reports-start-date"
              label="Start date"
              value={dateRange.startDate}
              onChange={(iso) =>
                setDateRange((current) => ({ ...current, startDate: iso }))
              }
            />
            <DatePicker
              mode="single"
              id="reports-end-date"
              label="End date"
              value={dateRange.endDate}
              onChange={(iso) =>
                setDateRange((current) => ({ ...current, endDate: iso }))
              }
            />
            {/* </div> */}
            <div className="flex items-end justify-end">
              <Button
                onClick={handleRefresh}
                isLoading={isRefreshing}
                size="xs"
                className="h-11 justify-bottom rounded-xl bg-emerald-600 text-sm hover:bg-emerald-700"
              >
                {!isRefreshing && <RefreshCw size={16} />}
                Refresh
              </Button>
            </div>
            {/* </div> */}
          </div>
        </div>

        <div
          aria-busy={isLoadingMetrics || undefined}
          className={`mt-6 grid grid-cols-1 gap-4 transition-opacity duration-150 motion-reduce:transition-none md:grid-cols-2 xl:grid-cols-4 ${isLoadingMetrics && metrics ? 'opacity-60' : ''}`}
        >
          {metrics
            ? metrics.map((metric) => (
                <SummaryMetricCard key={metric.label} {...metric} />
              ))
            : Array.from({ length: 4 }, (_, index) => (
                <div
                  key={index}
                  aria-hidden="true"
                  className="h-28 animate-pulse rounded-2xl border border-slate-100 bg-slate-50 motion-reduce:animate-none"
                />
              ))}
        </div>

        <div className="mt-6 grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="relative min-w-0 flex-1">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                size={16}
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                onKeyDown={handleSearchKeyDown}
                placeholder={searchPlaceholder}
                className="h-11 w-full rounded-xl border border-slate-200 bg-white pl-10 pr-3 text-sm text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
              />
            </div>
            {toolbarContent}
            {exportConfig && (
              <Button
                variant="outline"
                onClick={handleExportCsv}
                isLoading={isExporting}
                disabled={rows.length === 0}
                className="h-11 justify-center rounded-xl border border-emerald-200 bg-white px-4 text-sm text-emerald-700 hover:bg-emerald-50 hover:text-black sm:min-w-[160px]"
              >
                {!isExporting && <Download size={16} />}
                Export CSV
              </Button>
            )}
          </div>
          <p className="text-sm text-slate-500 lg:text-right">
            {isLoading && rows.length > 0 ? (
              <span className="inline-flex items-center gap-1.5 text-emerald-600">
                <RefreshCw size={12} className="animate-spin" />
                Updating results…
              </span>
            ) : (
              <>
                {totalCount !== null ? (
                  <>
                    Showing{' '}
                    <span className="font-semibold text-slate-900">
                      {rows.length.toLocaleString()}
                    </span>{' '}
                    of{' '}
                    <span className="font-semibold text-slate-900">
                      {totalCount.toLocaleString()}
                    </span>{' '}
                    records
                  </>
                ) : (
                  <>
                    Loaded{' '}
                    <span className="font-semibold text-slate-900">
                      {rows.length}
                    </span>{' '}
                    records
                  </>
                )}
                {searchMode === 'client' && searchQuery.trim() ? (
                  <>
                    {' '}
                    | Matching{' '}
                    <span className="font-semibold text-slate-900">
                      {filteredRows.length}
                    </span>
                  </>
                ) : null}
                {hasMore ? ' | Scroll to load more' : ''}
              </>
            )}
          </p>
        </div>
      </section>

      {(error || metricsError) && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error || metricsError}
        </div>
      )}

      {!isDateRangeValid ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
          Start date must be earlier than or equal to end date.
        </div>
      ) : filteredRows.length === 0 ? (
        <EmptyState title={emptyTitle} description={emptyDescription} />
      ) : (
        <section className="max-w-full overflow-hidden rounded-[22px] border border-slate-100 bg-white shadow-[0_2px_12px_rgba(15,23,42,0.04)]">
          <div
            ref={scrollContainerRef}
            className={`max-h-[70vh] overflow-auto transition-opacity duration-150 ${isLoading && rows.length > 0 ? 'opacity-60 pointer-events-none' : ''}`}
          >
            <table className="min-w-[1100px] w-full divide-y divide-slate-100">
              <thead className="sticky top-0 z-10 bg-slate-50/95 backdrop-blur">
                <tr>
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      className={`px-4 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 ${getReportAlignmentClass(column.align)} ${column.className || ''}`}
                    >
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {filteredRows.map((row) => (
                  <tr
                    key={getRowKey(row)}
                    className="hover:bg-slate-50/70 transition-colors"
                  >
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        className={`max-w-[16rem] whitespace-normal break-words px-4 py-3 align-top text-sm text-slate-700 ${getReportAlignmentClass(column.align)} ${column.className || ''}`}
                      >
                        {column.render(row)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {isLoadingMore ? (
              <div className="border-t border-slate-100 px-4 py-3 text-center text-sm text-slate-500">
                Loading more records...
              </div>
            ) : null}
          </div>
        </section>
      )}
    </div>
  );
};
